import React, { useEffect } from 'react';
import { Link, useBlocker } from 'react-router-dom';
import { AlertCircle, ArrowLeft, ArrowRight, CheckCircle, Loader2 } from 'lucide-react';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { LeaveWizardDialog } from '@/components/connectors/wizard/page/LeaveWizardDialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { splitFormData } from '@/lib/validation/splitFormData';
import { SelectConnectorType } from '@/components/connectors/wizard/SelectConnectorType';
import { ConfigureConnector } from '@/components/connectors/wizard/ConfigureConnector';
import { TestConnection } from '@/components/connectors/wizard/TestConnection';
import { ScheduleConnector } from '@/components/connectors/wizard/ScheduleConnector';
import { ConfirmConnector } from '@/components/connectors/wizard/ConfirmConnector';
import { WizardStepRail } from '@/components/connectors/wizard/page/WizardStepRail';
import { useCreateConnectorWizard } from '@/components/connectors/wizard/page/useCreateConnectorWizard';
import { PASSWORD_MANAGER_OPT_OUT_FORM } from '@/components/connectors/shared/passwordManagerOptOut';
import {
  DiscoveryGateProvider,
  useDiscoveryPending,
  useSubmitBlocked,
} from '@/lib/oauth/discoveryGate';

/**
 * Continue button that additionally stays disabled while any OAuth-populated
 * field in the current step is still discovering accounts (#34). Rendered inside
 * DiscoveryGateProvider so it can read the aggregate pending state.
 */
const WizardContinueButton: React.FC<{
  onClick: () => void;
  canProceed: boolean;
  isCreating: boolean;
}> = ({ onClick, canProceed, isCreating }) => {
  const { isAnyPending } = useDiscoveryPending();
  // Block Continue while a required OAuth-populated allow-list has nothing
  // selected (#1013) — the schema's required rule was never enforced for this
  // field type. Kept out of the pending spinner: the reason is rendered inline
  // on the field.
  const { isAnyBlocked } = useSubmitBlocked();
  return (
    <div className="flex items-center gap-3">
      {isAnyPending && (
        <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          Discovering accounts…
        </span>
      )}
      <Button
        onClick={onClick}
        disabled={!canProceed || isCreating || isAnyPending || isAnyBlocked}
      >
        Continue
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
};

export const ConnectorCreatePage: React.FC = () => {
  const w = useCreateConnectorWizard();
  const stepMeta = w.steps.find((s) => s.isActive) ?? w.steps[0] ?? { title: '', description: '' };

  // Land at the top of the wizard on entry. The app's data router has no scroll
  // restoration and the page scrolls the window, so arriving from a scrolled-down
  // list (e.g. a long Connectors page) would otherwise drop the user mid-page (#354).
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const scheduleStepId = w.steps.find((s) => s.key === 'schedule')?.id;

  // Warn before losing entered configuration on reload / tab-close / external
  // navigation (full unloads). In-app SPA navigation is handled by the blocker
  // below (#233 added beforeunload; #303 adds the data-router blocker).
  useUnsavedChangesGuard(w.isDirty);

  // Block in-app SPA navigation (sidebar links, Back/Forward, breadcrumb) while
  // dirty. getIsDirty() reads a ref synchronously so the post-create redirect
  // (which flips it first) is never blocked. See spec D2/D2a (#303).
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      w.getIsDirty() && currentLocation.pathname !== nextLocation.pathname,
  );

  const renderStep = () => {
    switch (w.currentStepKey) {
      case 'type':
        return <SelectConnectorType selectedType={w.selectedType} onSelectType={w.selectType} />;
      case 'configure':
        return w.selectedType ? (
          <ConfigureConnector
            connectorType={w.selectedType}
            formData={w.formData}
            onChange={w.updateForm}
            errors={w.formErrors}
            onValidate={() => true}
          />
        ) : null;
      case 'test':
        return w.selectedType ? (
          <TestConnection
            connectorType={w.selectedType}
            autoRun
            credentials={
              w.schema
                ? splitFormData(w.formData as unknown as Record<string, unknown>, w.schema.fields).credentials
                : undefined
            }
            onTestComplete={w.completeTest}
            onSkip={w.skipTest}
          />
        ) : null;
      case 'schedule':
        return w.selectedType ? (
          <ScheduleConnector
            connectorType={w.selectedType}
            formData={w.formData}
            onChange={w.updateForm}
          />
        ) : null;
      case 'review':
        return w.selectedType ? (
          <ConfirmConnector
            connectorType={w.selectedType}
            formData={w.formData}
            healthCheckResult={w.healthCheckResult}
            onEdit={w.goToStep}
            scheduleStepId={scheduleStepId}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto px-6 py-8">
      <nav className="mb-4 text-sm text-muted-foreground">
        <Link to="/connectors" className="hover:underline">Connectors</Link>
        <span className="mx-1">/</span>
        <span className="text-foreground">New connector</span>
      </nav>

      <h1 className="text-3xl font-bold">Add a connector</h1>
      <p className="mt-1 text-muted-foreground">Connect a new data source to your lakehouse</p>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:items-start lg:grid-cols-[200px_minmax(0,1fr)]">
        <WizardStepRail
          currentStep={w.currentStep}
          steps={w.steps}
          onStepClick={(id) => { if (id < w.currentStep) w.goToStep(id); }}
        />

        <DiscoveryGateProvider>
        <div className="min-w-0">
          <div className="mb-4">
            <h2 className="text-xl font-semibold">{stepMeta.title}</h2>
            <p className="text-sm text-muted-foreground">{stepMeta.description}</p>
          </div>

          <form {...PASSWORD_MANAGER_OPT_OUT_FORM} onSubmit={(e) => e.preventDefault()}>
            {renderStep()}
          </form>

          {w.currentStepKey === 'review' && w.createError && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>The connector was not created</AlertTitle>
              <AlertDescription>{w.createError}</AlertDescription>
            </Alert>
          )}

          <div className="mt-6 flex items-center justify-between border-t pt-4">
            <div>
              {w.currentStep > 1 && (
                <Button variant="ghost" onClick={w.goBack} disabled={w.isCreating}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {!w.isLastStep ? (
                <WizardContinueButton
                  onClick={w.goNext}
                  canProceed={w.canProceed}
                  isCreating={w.isCreating}
                />
              ) : (
                <Button onClick={w.create} disabled={w.isCreating}>
                  {w.isCreating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Create connector
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
        </DiscoveryGateProvider>

      </div>

      <LeaveWizardDialog
        open={blocker.state === 'blocked'}
        onStay={() => blocker.reset?.()}
        onLeave={() => blocker.proceed?.()}
      />
    </div>
  );
};
